import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import VoiceHost from './pages/VoiceHost';
import GameGuide from './pages/GameGuide';
import GameProgress from './pages/GameProgress';
import GameNotes from './pages/GameNotes';
import SpeechTimer from './pages/SpeechTimer';
import GameStats from './pages/GameStats';
import PlayerDetail from './pages/PlayerDetail';
import { Login } from './pages/Login';
import ProfilePage from './pages/ProfilePage';
import TestPage from './pages/OfflineGamePage';
import OnlineLobbyPage from './pages/OnlineLobby';
import OnlineGameRoom from './pages/OnlineGameRoom';
import OnlineGameProgress from './pages/OnlineGameProgress';
import GameDateDiagnose from './pages/GameDateDiagnose';
import BottomNav from './components/BottomNav';
import { SilentRefreshService } from './services/SilentRefreshService';
import { useEffect } from 'react';
import { hasSupabaseConfig } from './lib/supabase';

// V527: 初始化后台静默刷新服务
SilentRefreshService.getInstance().start();

// 定义不需要显示底部导航栏的路由
const HIDE_NAV_ROUTES = ['/online/room', '/online/game'];

function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const shouldHideNav = HIDE_NAV_ROUTES.some(route => location.pathname.startsWith(route));
  
  return (
    <div className="min-h-screen pb-[60px]">
      {children}
      {!shouldHideNav && <BottomNav />}
    </div>
  );
}

export default function App() {
  // V527: 页面可见性变化时控制静默刷新
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[V527] 页面隐藏，暂停静默刷新');
      } else {
        console.log('[V527] 页面可见，恢复静默刷新');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (!hasSupabaseConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#120f1d] via-[#1b1530] to-[#0e1a2d] text-white flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 text-center">
          <div className="text-4xl mb-4">A</div>
          <h1 className="text-2xl font-bold mb-3">克隆站已创建</h1>
          <p className="text-white/80 leading-7">
            当前页面已经独立部署完成，但还没有接入新的独立数据环境。
          </p>
          <p className="text-white/60 leading-7 mt-3">
            接下来只需要配置新的 Supabase 项目，这个克隆站就会完全脱离原应用单独运行。
          </p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/online-lobby" element={<OnlineLobbyPage />} />
          <Route path="/online/room/:roomId" element={<OnlineGameRoom />} />
          <Route path="/online/game/:sessionId" element={<OnlineGameProgress />} />
          <Route path="/host" element={<VoiceHost />} />
          <Route path="/guide" element={<GameGuide />} />
          <Route path="/progress" element={<GameProgress />} />
          <Route path="/offline-game" element={<TestPage />} />
          <Route path="/vote" element={<GameNotes />} />
          <Route path="/timer" element={<SpeechTimer />} />
          <Route path="/stats" element={<GameStats />} />
          <Route path="/player-detail" element={<PlayerDetail />} />
          <Route path="/player/:playerNumber" element={<PlayerDetail />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/diagnose" element={<GameDateDiagnose />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}
